/**
 * Assist anchor registry: every UI control the assistant can point at.
 *
 * A control opts in by spreading `assistAnchor(id)` onto the real element,
 * which renders `data-assist="<id>"`. The spotlight finds it with
 * `[data-assist="<CSS.escape(id)>"]`; the backend learns the same ids from
 * `public/assist-manifest.json`, which `scripts/build-assist-manifest.mjs`
 * builds from this module (`toManifestAnchors`). Contract:
 * `.superpowers/sdd/2026-09-24-ai-and-insights-plan/ASSIST-MANIFEST-schema.md`.
 *
 * Imports use relative paths with extensions and no `@/` alias, so Node can
 * load this module without Vite.
 */

import { rolesAtOrAbove } from './anchorRoles.js'
import { SHELL_ANCHORS, ASSISTANT_ANCHORS } from './entries/shell.js'
import { DASHBOARD_ANCHORS } from './entries/dashboards.js'
import { REPORT_ANCHORS } from './entries/reports.js'
import { METRIC_ANCHORS } from './entries/metrics.js'
import { SETTINGS_ANCHORS } from './entries/settings.js'
import { ROCK_SETUP_ANCHORS } from './entries/rockSetup.js'
import { ROCK_ONBOARDING_ANCHORS } from './entries/rockOnboarding.js'
import { ROCK_ONBOARDING_STEP_ANCHORS } from './entries/rockOnboardingSteps.js'

export { settingsAnchorId } from './entries/settings.js'

/** Anchor ids are dotted snake_case: safe in a CSS attribute selector and a URL. */
export const ASSIST_ID_PATTERN = /^[a-z0-9_.]+$/

/** Preconditions a spotlight must satisfy before the anchor can render. */
export const ASSIST_REQUIRES = Object.freeze(['edit_mode'])

/** Object kinds a `:id` route segment can stand for. */
export const ASSIST_ENTITIES = Object.freeze(['dashboard', 'report', 'metric', 'rock_connection'])

/**
 * Right on the `entity` object a person needs before the control renders for
 * them, beyond `minRole`: `view` (seeing the object is enough), `edit` (the
 * object's own edit right, e.g. `dashboard.can_edit`) or `manage` (owner or
 * tenant administrator: delete, transfer ownership). Every entity anchor
 * declares it; the backend's `show_me` refuses an anchor the person lacks it for.
 */
export const ASSIST_PERMISSIONS = Object.freeze(['view', 'edit', 'manage'])

/**
 * @typedef {Object} AssistAnchorEntry
 * @property {string} id - Matches `ASSIST_ID_PATTERN`.
 * @property {string} label - The control's visible wording.
 * @property {string} intent - What a person uses it for; searched by `ui_find`.
 * @property {string} route - `*` (every app page) or a route pattern with `:id`.
 * @property {'viewer'|'editor'|'admin'|'owner'} minRole
 * @property {'edit_mode'} [requires]
 * @property {string} [parent] - Anchor that reveals this one (a menu trigger or step).
 * @property {'dashboard'|'report'|'metric'|'rock_connection'} [entity] - What `:id` is.
 * @property {'view'|'edit'|'manage'} [permission] - Right needed on `entity`; required when `entity` is set.
 * @property {string} [seededFrom] - Source list for generated entries (not in the manifest).
 */

/** @type {AssistAnchorEntry[]} */
const ENTRIES = [
  ...SHELL_ANCHORS,
  ...ASSISTANT_ANCHORS,
  ...DASHBOARD_ANCHORS,
  ...REPORT_ANCHORS,
  ...METRIC_ANCHORS,
  ...SETTINGS_ANCHORS,
  ...ROCK_SETUP_ANCHORS,
  ...ROCK_ONBOARDING_ANCHORS,
  ...ROCK_ONBOARDING_STEP_ANCHORS,
]

/**
 * @param {AssistAnchorEntry} entry
 * @param {Set<string>} ids - every registered id
 * @returns {string[]} problems, empty when valid
 */
function entryProblems(entry, ids) {
  const problems = []
  if (!ASSIST_ID_PATTERN.test(entry.id)) problems.push('id must match ^[a-z0-9_.]+$')
  if (!entry.label || !entry.intent) problems.push('label and intent are required')
  if (entry.route !== '*' && !entry.route?.startsWith('/')) problems.push('route must be "*" or start with "/"')
  if (entry.requires && !ASSIST_REQUIRES.includes(entry.requires)) problems.push(`unknown requires "${entry.requires}"`)
  if (entry.entity && !ASSIST_ENTITIES.includes(entry.entity)) problems.push(`unknown entity "${entry.entity}"`)
  if (entry.route?.includes(':id') && !entry.entity) problems.push('a :id route needs an entity')
  if (entry.entity && !entry.permission) problems.push('an entity anchor needs a permission')
  if (entry.permission && !ASSIST_PERMISSIONS.includes(entry.permission)) problems.push(`unknown permission "${entry.permission}"`)
  if (entry.permission && !entry.entity) problems.push('permission needs an entity')
  if (entry.parent && !ids.has(entry.parent)) problems.push(`parent "${entry.parent}" is not registered`)
  try {
    rolesAtOrAbove(entry.minRole)
  } catch (error) {
    problems.push(error.message)
  }
  return problems
}

/**
 * Builds the frozen registry, failing loudly on a malformed or duplicate
 * entry: the registry is static, so any problem is a programming error.
 *
 * @param {AssistAnchorEntry[]} entries
 * @returns {Readonly<Record<string, Readonly<AssistAnchorEntry>>>}
 */
export function buildRegistry(entries) {
  const idList = entries.map((entry) => entry.id)
  const duplicates = idList.filter((id, index) => idList.indexOf(id) !== index)
  if (duplicates.length) throw new Error(`assist: duplicate anchor ids: ${duplicates.join(', ')}`)
  const ids = new Set(idList)
  const problems = entries.flatMap((entry) => entryProblems(entry, ids).map((problem) => `${entry.id}: ${problem}`))
  if (problems.length) throw new Error(`assist: invalid anchors:\n${problems.join('\n')}`)
  return Object.freeze(Object.fromEntries(entries.map((entry) => [entry.id, Object.freeze({ ...entry })])))
}

/** Every anchor, keyed by id, in registry order. */
export const ASSIST_ANCHORS = buildRegistry(ENTRIES)

/**
 * @param {string} id
 * @returns {boolean}
 */
export function isAssistAnchorId(id) {
  return Object.hasOwn(ASSIST_ANCHORS, id)
}

/** Vite sets `import.meta.env.DEV`; plain Node (the manifest build) has no `env`. */
const IS_DEV = Boolean(import.meta.env?.DEV)

/**
 * Props that mark an element as an assist anchor. Spread them onto the real
 * interactive element (not a wrapper) so the spotlight rings the control
 * itself: `<Button {...assistAnchor('reports.new')}>`.
 *
 * @param {string} id - A registered anchor id.
 * @returns {{'data-assist': string}}
 * @throws {Error} in development when `id` isn't registered, so a typo or a
 *   removed entry fails on first render instead of silently drifting from
 *   the manifest. Production renders the attribute regardless.
 */
export function assistAnchor(id) {
  if (IS_DEV && !isAssistAnchorId(id)) {
    throw new Error(`assistAnchor: unknown anchor id "${id}" - add it to src/features/assist/entries/`)
  }
  return { 'data-assist': id }
}

/**
 * The manifest's `anchors` array: registry order, `minRole` expanded to the
 * explicit `roles` list, internal fields dropped, optional keys omitted when
 * unset.
 *
 * @param {Readonly<Record<string, AssistAnchorEntry>>} [registry=ASSIST_ANCHORS]
 * @returns {Array<Object>}
 */
export function toManifestAnchors(registry = ASSIST_ANCHORS) {
  return Object.values(registry).map(({ id, label, intent, route, minRole, requires, parent, entity, permission }) => ({
    id,
    label,
    intent,
    route,
    roles: rolesAtOrAbove(minRole),
    ...(requires ? { requires } : {}),
    ...(parent ? { parent } : {}),
    ...(entity ? { entity } : {}),
    ...(permission ? { permission } : {}),
  }))
}
